import { useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";

import {
  TILE_SET_DYNAMIC_BADGE,
  TILE_SET_DYNAMIC_DESCRIPTION,
} from "@/lib/tileSetUiLabels";
import { getMapChromeTheme } from "@/components/mapChromeTheme";

function QuestionMarkIcon() {
  return (
    <span
      className="inline-flex h-3 min-w-3 shrink-0 items-center justify-center rounded-full border border-current/45 text-[8px] font-bold leading-none opacity-90"
      aria-hidden
    >
      ?
    </span>
  );
}

export type DynamicBrushBadgeProps = {
  className?: string;
  onActivate: () => void;
  label?: string;
  description?: string;
  /**
   * When true, renders only the first letter of `label` and hides the help icon.
   * The full label and description remain available via tooltip and aria-label.
   */
  compact?: boolean;
};

type TooltipPosition = {
  top: number;
  left: number;
};

export function DynamicBrushBadge({
  className = "",
  onActivate,
  label = TILE_SET_DYNAMIC_BADGE,
  description = TILE_SET_DYNAMIC_DESCRIPTION,
  compact = false,
}: DynamicBrushBadgeProps) {
  const displayLabel = compact ? label.charAt(0) : label;
  const theme = getMapChromeTheme();
  const badgeRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLSpanElement>(null);
  const tooltipId = useId();
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState<TooltipPosition | null>(null);

  const updateTooltipPosition = useCallback(() => {
    const badge = badgeRef.current;
    const tooltip = tooltipRef.current;
    if (!badge || !tooltip) {
      return;
    }

    const viewportPadding = 12;
    const gap = 8;
    const badgeRect = badge.getBoundingClientRect();
    const tooltipRect = tooltip.getBoundingClientRect();

    let left = badgeRect.right - tooltipRect.width;
    left = Math.max(viewportPadding, left);
    left = Math.min(left, window.innerWidth - tooltipRect.width - viewportPadding);

    let top = badgeRect.top - tooltipRect.height - gap;
    if (top < viewportPadding) {
      top = badgeRect.bottom + gap;
    }
    top = Math.min(top, window.innerHeight - tooltipRect.height - viewportPadding);

    setTooltipPosition({ top, left });
  }, []);

  useEffect(() => {
    if (!isTooltipVisible) {
      return;
    }

    const update = () => updateTooltipPosition();
    const frame = window.requestAnimationFrame(update);
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [isTooltipVisible, updateTooltipPosition]);

  return (
    <>
      <span
        ref={badgeRef}
        className={`${theme.cardBadge} z-20 flex cursor-default select-none items-center gap-0.5 whitespace-nowrap px-1 py-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3e664d] ${className}`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onActivate();
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            e.stopPropagation();
            onActivate();
          }
        }}
        onMouseEnter={() => setIsTooltipVisible(true)}
        onMouseLeave={() => setIsTooltipVisible(false)}
        onFocus={() => setIsTooltipVisible(true)}
        onBlur={() => setIsTooltipVisible(false)}
        role="button"
        tabIndex={0}
        aria-label={`${label}. ${description}`}
        aria-describedby={isTooltipVisible ? tooltipId : undefined}
      >
        <span>{displayLabel}</span>
        {compact ? null : <QuestionMarkIcon />}
      </span>
      {isTooltipVisible
        ? createPortal(
            <span
              ref={tooltipRef}
              id={tooltipId}
              className={`pointer-events-none fixed z-[120] w-max max-w-[min(240px,calc(100vw-2rem))] px-2 py-1.5 text-left text-[11px] font-normal leading-snug ${theme.tooltip}`}
              role="tooltip"
              style={{
                top: tooltipPosition?.top ?? -9999,
                left: tooltipPosition?.left ?? -9999,
                visibility: tooltipPosition ? "visible" : "hidden",
              }}
            >
              {description}
            </span>,
            document.body,
          )
        : null}
    </>
  );
}
